/**
 * How a replay playhead move has to reach BankMessagesContext.
 *
 * `"step"` is an incremental UPDATE_MESSAGE — the cheap path ordinary playback
 * takes frame after frame. `"rebuild"` is a REPLAY_SEEK that refolds records
 * `0..index` from a pristine state.
 */
export type ReplayMoveKind = "step" | "rebuild";

/**
 * Decide which one a move to `index` needs.
 *
 * Only the record immediately after the one last dispatched continues the
 * accumulated fold. Everything else is a discontinuity — a step backwards, the
 * wrap from the last record round to the first, or the first dispatch after a
 * filter change (signalled by a null marker) — and cannot be patched in.
 *
 * The reason is that the context accumulates in ways nothing but a rebuild can
 * undo: `byLift` is pruned only by CLEAR_MESSAGES, and the four CDO maps plus
 * `everReceived` only ever merge. So after a backward move the bank roll-up
 * would still be unioning lift snapshots taken from records the new position
 * has not reached, and no later message would clear them — a sibling car that
 * the scrubbed-to record never mentions keeps its future hall call for as long
 * as the provider stays mounted.
 */
export function replayMoveKind(lastDispatchedIndex: number | null, index: number): ReplayMoveKind {
  return lastDispatchedIndex !== null && lastDispatchedIndex === index - 1 ? "step" : "rebuild";
}
